import type { Order } from '@/mocks/orders';
import { statusLabels, statusColors } from '@/mocks/orders';

interface OrderCardProps {
  order: Order;
  onSelect: (order: Order) => void;
}

export default function OrderCard({ order, onSelect }: OrderCardProps) {
  const whatsappUrl = `https://wa.me/${order.phone}`;

  return (
    <div
      className="bg-white rounded-xl border border-stone-200/80 p-4 hover:border-stone-300 transition-all cursor-pointer"
      onClick={() => onSelect(order)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono font-semibold text-stone-800">{order.id}</span>
            {order.isUrgent && (
              <span className="text-[10px] bg-status-red/10 text-status-red px-1.5 py-0.5 rounded font-medium pulse-warning">ACİL</span>
            )}
            {order.isLate && (
              <span className="text-[10px] bg-status-red/10 text-status-red px-1.5 py-0.5 rounded font-medium">GECİKMİŞ</span>
            )}
          </div>
          <span className="text-sm font-semibold text-stone-800 block">{order.customerName}</span>
        </div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[order.status]}`}>
          {statusLabels[order.status]}
        </span>
      </div>

      {/* Details */}
      <div className="space-y-1.5 mb-3">
        <div className="flex items-center gap-2 text-xs">
          <i className="ri-smartphone-line text-stone-400" />
          <span className="text-stone-500">{order.phone.replace('90', '0')}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <i className="ri-map-pin-line text-stone-400" />
          <span className="text-stone-500">{order.city}/{order.district}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <i className="ri-shopping-bag-line text-stone-400" />
          <span className="text-stone-600 truncate">{order.productName}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <i className="ri-calendar-line text-stone-400" />
          <span className="text-stone-500">{order.orderDate}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-stone-100">
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-stone-800">{order.amount.toLocaleString('tr-TR')} ₺</span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            order.paymentStatus === 'tahsil_edildi' ? 'bg-status-green/10 text-status-green' :
            order.paymentStatus === 'bekleniyor' ? 'bg-status-orange/10 text-status-orange' :
            'bg-status-gray/10 text-status-gray'
          }`}>
            {order.paymentType === 'kapida_odeme' ? 'Kapıda Ödeme' : order.paymentType === 'havale' ? 'Havale' : 'Kredi Kartı'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="nofollow noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
          >
            <i className="ri-whatsapp-line text-base" />
          </a>
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(order); }}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-100 text-stone-500 hover:bg-stone-200 transition-colors"
          >
            <i className="ri-arrow-right-s-line text-base" />
          </button>
        </div>
      </div>
    </div>
  );
}